import { enqueueSnackbar } from 'notistack';
import { isEmpty } from 'ramda';
import { createAction } from 'redux-actions';

import { getMessage } from '@gef-ui/localization/messages';

import { appendMode } from 'constants/general';
import fetch from 'features/common/fetch';
import { pushToFilterDataLayer } from 'features/common/gtm/utils';
// eslint-disable-next-line import/no-cycle
import producedProduction from 'features/views/production/common';
import { getHasOrgStructureFilledToSeller } from 'features/views/production/common/utils';
import { PRODUCED_PRODUCTION_FILTER_FORM_NAME } from 'features/views/production/documents/form';
import user from 'features/views/user';
import { trimFormData } from 'utils';

import * as types from './actionTypes';
import { formDataFieldsMap } from './constants';
import { getSort } from './selectors';
import { queryParamsFactory, requestBodyFactory } from './utils';

export const appendDocuments = createAction(types.APPEND_DOCUMENTS, (documents) => ({ documents }));
export const reset = createAction(types.RESET);
export const setDocuments = createAction(types.SET_DOCUMENTS, (documents) => ({ documents }));
export const setSort = createAction(types.SET_SORT, (sort) => ({ sort }));
export const toggleDocumentToDelete = createAction(types.TOGGLE_DOCUMENT_TO_DELETE, (id) => ({ id }));

const url = fetch.constants.urls.productionDocuments;

export const load =
	({ queryParams, body, callGtmFilter, formData }, append, userStructure) =>
	(dispatch) => {
		dispatch(
			fetch.actions.post(url, {
				takeLatest: true,
				uniqueReqID: 'overview',
				body,
				queryParams,
				onSuccess: [
					(response) => () => {
						const data = response.body.data;
						if (!data || !Array.isArray(data)) return;

						if (getHasOrgStructureFilledToSeller(userStructure)) {
							dispatch(producedProduction.actions.enableActions());
						} else {
							dispatch(producedProduction.actions.disableActionButtons());
						}
						dispatch(append === appendMode.APPEND ? appendDocuments(data) : setDocuments(data));

						if (callGtmFilter) {
							pushToFilterDataLayer({
								filterSection: 'Vykázané dokumenty',
								formData,
								formDataFieldsMap,
							});
						}
					},
				],
			}),
		);
	};

export const reload =
	() =>
	async (dispatch, getState, { getFormData }) => {
		const lastRequest = fetch.selectors.getLastRequest(`${url}.overview`)(getState());
		const formData = await getFormData(PRODUCED_PRODUCTION_FILTER_FORM_NAME);
		const userStructure = {
			channelId: formData.channel,
			regionId: formData.region,
			agencyId: formData.agency,
			managerLogin: formData.manager,
			sellerLogin: formData.seller,
		};

		if (isEmpty(lastRequest)) return;
		dispatch(load(lastRequest, appendMode.SET, userStructure));
	};

export const loadPage =
	(page) =>
	async (dispatch, getState, { getFormData }) => {
		const { queryParams, body } = fetch.selectors.getLastRequest(`${url}.overview`)(getState());
		const formData = await getFormData(PRODUCED_PRODUCTION_FILTER_FORM_NAME);

		const userStructure = {
			channelId: formData.channel,
			regionId: formData.region,
			agencyId: formData.agency,
			managerLogin: formData.manager,
			sellerLogin: formData.seller,
		};

		const updatedPayload = {
			queryParams: {
				...queryParams,
				page,
			},
			body,
		};

		dispatch(load(updatedPayload, appendMode.SET, userStructure));
	};

export const loadMore =
	() =>
	async (dispatch, getState, { getFormData }) => {
		const { queryParams, body } = fetch.selectors.getLastRequest(`${url}.overview`)(getState());
		const formData = await getFormData(PRODUCED_PRODUCTION_FILTER_FORM_NAME);

		const userStructure = {
			channelId: formData.channel,
			regionId: formData.region,
			agencyId: formData.agency,
			managerLogin: formData.manager,
			sellerLogin: formData.seller,
		};

		const updatedPayload = {
			queryParams: {
				...queryParams,
				page: queryParams.page + 1,
			},
			body,
		};

		dispatch(load(updatedPayload, appendMode.APPEND, userStructure));
	};

export const loadSorted =
	(sort) =>
	async (dispatch, getState, { getFormData }) => {
		const { queryParams, body } = fetch.selectors.getLastRequest(`${url}.overview`)(getState());
		const formData = await getFormData(PRODUCED_PRODUCTION_FILTER_FORM_NAME);

		const userStructure = {
			channelId: formData.channel,
			regionId: formData.region,
			agencyId: formData.agency,
			managerLogin: formData.manager,
			sellerLogin: formData.seller,
		};

		const updatedPayload = {
			queryParams: {
				...queryParams,
				sort,
			},
			body,
		};

		dispatch(load(updatedPayload, appendMode.SET, userStructure));
	};

export const sortRecords = (sort) => (dispatch) => {
	dispatch(setSort(sort));
	dispatch(loadSorted(sort));
};

export const submitDocuments =
	() =>
	async (dispatch, getState, { getFormData }) => {
		dispatch(setSort(''));
		const data = await getFormData(PRODUCED_PRODUCTION_FILTER_FORM_NAME);
		const formData = trimFormData(data);
		const userRole = user.selectors.getCurrentRole(getState());
		const queryParams = queryParamsFactory(getSort(getState()));
		const body = requestBodyFactory(formData, userRole);

		// base userStructure is same, but we need structure defined from form - so we are using formData here
		const userStructure = {
			channelId: formData.channel,
			regionId: formData.region,
			agencyId: formData.agency,
			managerLogin: formData.manager,
			sellerLogin: formData.seller,
		};
		dispatch(load({ queryParams, body, callGtmFilter: true, formData }, appendMode.SET, userStructure));
	};

export const deleteDocuments = (documentIds, userRole) => async (dispatch) => {
	return dispatch(
		fetch.actions.put(fetch.constants.urls.productionDocumentsDelete, {
			body: {
				userRole,
				documentIds,
			},
			onSuccess: [
				() => () => {
					dispatch(reload());
					enqueueSnackbar(getMessage('toastr:approveDeleteDocument'), { variant: 'success' });
				},
			],
		}),
	);
};
